import { FormFieldProps } from 'lib/forms/FormFieldProps';
import React, { useState, useEffect } from 'react';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';

import { useFormikContext, FormikValues } from 'formik';
import Headline from 'src/helpers/Headline/Headline';
import { Subheadline } from 'src/helpers/Subheadline';
import { pricingData } from 'lib/utils/temp-pricingdata';
import { EnumField, getEnum } from 'lib/utils/get-enum';
import { Item } from '@sitecore-jss/sitecore-jss-nextjs';

type PricingEstimateSummaryProps =
  Feature.EnterpriseWeb.RenewalByAndersen.Forms.Fields.PricingEstimateSummary & FormFieldProps;

type estimatorPricingProduct = Item & {
  fields?: {
    estimateSummarySection: EnumField<string>;
    pricingProductId: EnumField<string>;
  };
};

type LineItem = {
  label: string;
  qty?: number;
  min: number;
  max: number;
};

type SummaryItem = LineItem & {
  lineItems?: LineItem[];
};

type SummaryLineItems = SummaryItem[];
type SummaryField = {
  fields: {
    fieldName: { value: string };
    label: { value: string };
    estimateSummarySection: EnumField<string>;
    estimatorPricingProduct: estimatorPricingProduct;
  };
};

const totalEstimateLabel = 'Total Estimate';

const sumLineItems = (lineItems: LineItem[] = []): Pick<LineItem, 'min' | 'max' | 'qty'> => {
  return lineItems.reduce(
    (totals, item) => ({
      min: totals.min + item.min * (item.qty ?? 0),
      max: totals.max + item.max * (item.qty ?? 0),
      qty: (totals.qty ?? 0) + (item.qty ?? 0),
    }),
    { min: 0, max: 0, qty: 0 }
  );
};

const upsertSectionLineItem = (
  summaryLineItems: SummaryLineItems,
  sectionName: string,
  lineItem: LineItem
) => {
  const sectionIndex = summaryLineItems.findIndex((item) => item.label === sectionName);

  if (sectionIndex === -1) {
    summaryLineItems.push({
      label: sectionName,
      min: lineItem.min,
      max: lineItem.max,
      qty: lineItem.qty,
      lineItems: [lineItem],
    });
    return;
  }

  const section = summaryLineItems[sectionIndex];
  const sectionLineItems = section.lineItems ?? [];
  const lineItemIndex = sectionLineItems.findIndex((item) => item.label === lineItem.label);

  if (lineItemIndex === -1) {
    sectionLineItems.push(lineItem);
  } else {
    sectionLineItems[lineItemIndex] = lineItem;
  }

  section.lineItems = sectionLineItems;
  const sectionTotals = sumLineItems(sectionLineItems);
  section.min = sectionTotals.min;
  section.max = sectionTotals.max;
  section.qty = sectionTotals.qty;
};

const updateAggregateSectionTotals = (summaryLineItems: SummaryLineItems) => {
  summaryLineItems.forEach((section) => {
    if (!section.lineItems?.length || section.label === totalEstimateLabel) {
      return;
    }

    const sectionTotals = sumLineItems(section.lineItems);
    section.min = sectionTotals.min;
    section.max = sectionTotals.max;
    section.qty = sectionTotals.qty;
  });
};

const calculateTotalEstimate = (
  summaryLineItems: SummaryLineItems
): Pick<LineItem, 'min' | 'max' | 'qty'> => {
  return summaryLineItems.reduce(
    (totals, section) => {
      if (section.label === totalEstimateLabel) {
        return totals;
      }

      return {
        min: totals.min + section.min,
        max: totals.max + section.max,
        qty: (totals.qty ?? 0) + (section.qty ?? 0),
      };
    },
    { min: 0, max: 0, qty: 0 }
  );
};

const updateTotalEstimateSection = (summaryLineItems: SummaryLineItems) => {
  const totalEstimate = calculateTotalEstimate(summaryLineItems);
  const totalEstimateIndex = summaryLineItems.findIndex(
    (section) => section.label === totalEstimateLabel
  );

  if (totalEstimateIndex === -1) {
    summaryLineItems.push({
      label: totalEstimateLabel,
      min: totalEstimate.min,
      max: totalEstimate.max,
      qty: totalEstimate.qty,
    });
    return;
  }

  summaryLineItems[totalEstimateIndex].min = totalEstimate.min;
  summaryLineItems[totalEstimateIndex].max = totalEstimate.max;
  summaryLineItems[totalEstimateIndex].qty = totalEstimate.qty;
  summaryLineItems.push(summaryLineItems.splice(totalEstimateIndex, 1)[0]);
};

const flattenSummaryLineItems = (summaryLineItems: SummaryLineItems): Record<string, string> => {
  return summaryLineItems.reduce<Record<string, string>>((flat, section) => {
    if (section.min === 0 && section.max === 0) {
      return flat;
    }
    flat[section.label + 'Estimate'] = formatCurrencyRange(section.min, section.max);
    flat[section.label + 'EstimateQty'] = `${section.qty ?? 0}`;

    section.lineItems?.forEach((lineItem) => {
      if (lineItem.min > 0 && lineItem.max > 0) {
        flat[`${section.label}${lineItem.label}UnitPrice`] = formatCurrencyRange(
          lineItem.min,
          lineItem.max
        );
      }
    });

    return flat;
  }, {});
};

const getProductTotal = (field: SummaryField, values: FormikValues): LineItem | null => {
  const product = pricingData.data.affiliatePricing.find(
    (item) =>
      item.productId.toString() ===
      getEnum(field.fields.estimatorPricingProduct.fields.pricingProductId)
  );

  if (!product) {
    return null;
  }

  let qty = values[field.fields.fieldName.value];
  qty = Number.isInteger(qty) ? qty : 0;

  const tier = product.productPricing.find((item) => qty >= item.minQty && qty <= item.maxQty);
  const minPrice = tier?.minPrice ?? 0;
  const maxPrice = tier?.maxPrice ?? 0;

  return {
    label: field.fields.label.value,
    qty: qty,
    min: minPrice,
    max: maxPrice,
  };
};
const formatCurrencyRange = (min: number, max: number, suffix = '') => {
  if (min > 0 && max > 0) {
    const formattedMin = min.toLocaleString('en-US');
    const formattedMax = max.toLocaleString('en-US');
    return ` $${formattedMin} - $${formattedMax}${suffix}`;
  }
  return '';
};

const renderSummaryLineItemsByTier = (summaryLineItems: SummaryLineItems) => {
  return summaryLineItems.map((section) => {
    const tiers = section.lineItems; //?.filter((lineItem) => lineItem.min > 0 && lineItem.max > 0);

    return (
      <>
        <section key={section.label} className="flex w-full flex-col gap-1 py-2">
          <div className="flex w-full items-center justify-between text-body font-bold">
            <span>{section.label}</span>
            <span>{formatCurrencyRange(section.min, section.max)}</span>
          </div>
          <ul className="w-full">
            {section.label !== totalEstimateLabel ? (
              tiers?.map((tier, index) => (
                <li
                  className="pricing-estimate-summary__tier flex items-center justify-between py-0.5 pl-4 text-legal text-dark-gray"
                  key={`${section.label}-${tier.label}-${index}`}
                >
                  <span>
                    {tier.label} ({tier.qty ?? 0})
                  </span>
                  <span>{formatCurrencyRange(tier.min, tier.max, ' per unit')}</span>
                </li>
              ))
            ) : (
              <li className="pl-4 text-legal text-dark-gray">
                <span>Product Count: {section.qty ?? 0}</span>
              </li>
            )}
          </ul>
        </section>
        <hr className="my-2 w-full border-b-[.5px] border-gray" />
      </>
    );
  });
};

const PricingEstimateSummary = (props: PricingEstimateSummaryProps) => {
  const [summaryLineItems, setSummaryLineItems] = useState<SummaryLineItems>([
    { label: totalEstimateLabel, min: 0, max: 0, qty: 0 },
  ]);
  const { values, setFieldValue } = useFormikContext<FormikValues>();

  useEffect(() => {
    if (!props.fields) {
      return;
    }

    setSummaryLineItems((prev) => {
      const updated = prev; //cloneSummaryLineItems(prev);

      props.fields.fieldsToSummarize.forEach((field: SummaryField) => {
        if (!(field.fields.fieldName.value in values)) {
          return;
        }

        const total = getProductTotal(field, values);

        if (!total) {
          return;
        }

        const sectionName =
          getEnum(field.fields.estimatorPricingProduct.fields.estimateSummarySection) ?? 'Windows';

        upsertSectionLineItem(updated, sectionName, total);
      });

      updateAggregateSectionTotals(updated);
      updateTotalEstimateSection(updated);

      const flattenedSummary = flattenSummaryLineItems(updated);
      for (const key in flattenedSummary) {
        setFieldValue(`WindowsProblems-${key}`, flattenedSummary[key], false);
      }

      return updated;
    });
  }, []);

  if (!props.fields) {
    return <></>;
  }

  return (
    <div className="col-span-12 w-full">
      {!props.fields.hideSummary?.value && (
        <div className="pricing-estimate-summary my-8 w-full border-t-[6px] border-t-primary py-2 md:w-[70%]">
          <Headline {...props} classes="text-large-body mt-m font-medium" />
          <Subheadline classes={'mt-s pl-4'} {...props} />
          <hr className="my-2 w-full border-b-[.5px] border-gray" />

          {renderSummaryLineItemsByTier(summaryLineItems)}
        </div>
      )}
    </div>
  );
};

export default PricingEstimateSummary;
